import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import { useStyles } from '../styles';
import { MetadataKeyValuePair } from '../types';
import {
  findDuplicateMetadataKeys,
  validateLabelValue,
  validateMetadataKey,
} from '../validation';

export interface MetadataKeyValueInputProps {
  inputType: 'annotation' | 'label';
  title?: string;
  description?: string;
  items: MetadataKeyValuePair[];
  disabled: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (
    index: number,
    property: keyof MetadataKeyValuePair,
    value: string,
  ) => void;
  errors: string[];
  enableAppSetSupport?: boolean;
}

export const MetadataKeyValueInput = (props: MetadataKeyValueInputProps) => {
  const {
    inputType,
    items,
    disabled,
    onAdd,
    onRemove,
    onChange,
    errors,
    enableAppSetSupport = false,
  } = props;
  const { classes, cx } = useStyles();

  const inputTypeCapitalized =
    inputType.charAt(0).toUpperCase() + inputType.slice(1);

  const isValidKeyInput = (value: string) => {
    return validateMetadataKey(value);
  };

  const valueMaxLength =
    inputType === 'label' && !enableAppSetSupport ? 63 : undefined;

  return (
    <>
      {items.map((item, index) => (
        <Box key={index} id={`${inputType}-${index}`}>
          <Box className={classes.flexContainer}>
            <Box
              className={cx(
                classes.flexContainer,
                classes.inputContainer,
                classes.keyValuePairContainer,
              )}
            >
              <TextField
                label={`${inputTypeCapitalized} Key`}
                fullWidth
                variant="outlined"
                size="small"
                error={
                  Boolean(errors?.length > 0) &&
                  (!item.key ||
                    !isValidKeyInput(item.key) ||
                    findDuplicateMetadataKeys(items).includes(item.key.trim()))
                }
                placeholder="e.g., custom.tomtom.io/my-metadata"
                slotProps={{ htmlInput: { maxLength: 63 } }}
                required
                value={item.key}
                onChange={e => onChange(index, 'key', e.target.value)}
              />
              <TextField
                label={`${inputTypeCapitalized} Value`}
                fullWidth
                variant="outlined"
                size="small"
                error={
                  Boolean(errors?.length > 0) &&
                  inputType === 'label' &&
                  Boolean(item.value) &&
                  !validateLabelValue(item.value, { enableAppSetSupport })
                }
                placeholder="e.g., my-app"
                slotProps={{ htmlInput: { maxLength: valueMaxLength } }}
                required
                value={item.value}
                onChange={e => onChange(index, 'value', e.target.value)}
              />
            </Box>
            <Box className={classes.removeButtonContainer}>
              <Button
                variant="outlined"
								size="small"
                color="secondary"
                onClick={() => onRemove(index)}
                disabled={disabled}
              >
                Remove
              </Button>
            </Box>
          </Box>
        </Box>
      ))}
      <Box className={classes.inputContainer}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAdd}
					size="small"
          color="secondary"
          disabled={disabled}
        >
          {`Add ${inputType}`}
        </Button>
      </Box>
    </>
  );
};
